import React from 'react';
import { API } from '../api';
import { normalizeMembershipTypePricing, formatMembershipTypeOptionLabel } from '../utils/membership';
import { useSiteMembershipDefaults } from '../utils/siteDefaults';
import ClubNewsCard from './ClubNewsCard';
import ClubPicker, { rememberClub, rememberedClub, clubLogoUrl } from './ClubPicker';
import MembershipTypeInfo from './MembershipTypeInfo';
import { START_YEAR_FIELD, asksStartYear, typesWithoutStartYear } from '../utils/playerTypes';

/* A newsletter's "Subscribe to club news" link lands on /subscribe/:clubId. */
const subscribeClubId = (() => {
  const m = typeof window !== 'undefined' && window.location.pathname.match(/^\/subscribe\/(\d+)\/?$/);
  return m ? m[1] : '';
})();

/* ====================================================================
   AUTH SCREEN — login / register inline
   ==================================================================== */
function AuthScreen({ mode: initialMode, onLogin, onRegister, onSwitch, regCompId }) {
  const siteDefaults = useSiteMembershipDefaults();
  const [mode, setMode] = React.useState(initialMode || 'login');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  /* Set when signing up found a profile the club already made: a link has
     gone to that address and nobody is signed in yet. */
  const [claimNotice, setClaimNotice] = React.useState('');
  const [clubId, setClubId] = React.useState('');
  const [regClubs, setRegClubs] = React.useState([]);
  const [registerMembershipMeta, setRegisterMembershipMeta] = React.useState(null);
  const [registerMembershipForm, setRegisterMembershipForm] = React.useState({});
  const [registerMembershipLoading, setRegisterMembershipLoading] = React.useState(false);
  const [memberClubId, setMemberClubId] = React.useState('');
  const [membershipMeta, setMembershipMeta] = React.useState(null);
  const [membershipLoading, setMembershipLoading] = React.useState(false);
  const [memberForm, setMemberForm] = React.useState({});
  const [memberSubmitting, setMemberSubmitting] = React.useState(false);
  const [memberError, setMemberError] = React.useState('');
  const [memberSuccess, setMemberSuccess] = React.useState(false);
  const [memberAlreadyMember, setMemberAlreadyMember] = React.useState('no');
  const [memberAccountMode, setMemberAccountMode] = React.useState('silent-login');
  const [memberSubmitNotice, setMemberSubmitNotice] = React.useState('');
  /* Set when the club already has a profile for this email. Holds the name
     on that profile, so the applicant can tell "that is me" from "that is
     my husband" — which is the decision being put to them. */
  const [memberDuplicate, setMemberDuplicate] = React.useState(null);
  const [honeypotWebsite, setHoneypotWebsite] = React.useState('');
  /* The club this visitor is here for. The front door is a picker of clubs;
     once one is chosen this page is that club's login, and the choice is
     remembered for next time. A newsletter's subscribe link says which club
     it is from, so it arrives already chosen. */
  const [chosenId, setChosenId] = React.useState(() => subscribeClubId || rememberedClub());
  const [clubsLoaded, setClubsLoaded] = React.useState(false);
  // A logo that will not load shows the GreenSheet mark instead of a broken image.
  const [brokenLogo, setBrokenLogo] = React.useState(null);

  React.useEffect(() => {
    fetch(`${API}/api/auth/clubs/public`)
      .then(r => r.ok ? r.json() : [])
      .then((clubs) => {
        const safeClubs = Array.isArray(clubs) ? clubs : [];
        setRegClubs(safeClubs);
        // One club: nothing to choose. A remembered club that has since gone, or been hidden, is forgotten.
        if (safeClubs.length === 1) setChosenId(String(safeClubs[0].id));
        else setChosenId(prev => (safeClubs.some(c => String(c.id) === String(prev)) ? prev : ''));
        if (safeClubs.length === 1) {
          setClubId(String(safeClubs[0].id));
          setMemberClubId(String(safeClubs[0].id));
          return;
        }
        if (safeClubs.length > 0) {
          setMemberClubId(prev => prev || String(safeClubs[0].id));
        }
      })
      .catch(() => {})
      .finally(() => setClubsLoaded(true));
  }, []);

  // The chosen club is the one registering joins and membership applies to.
  React.useEffect(() => {
    if (!chosenId) return;
    setClubId(String(chosenId));
    setMemberClubId(String(chosenId));
  }, [chosenId]);

  const chosenClub = regClubs.find(c => String(c.id) === String(chosenId)) || null;
  /* Choosing a club always lands on its Log In. The form the visitor was
     last on is remembered across the picker — so after "Create an account"
     and then "Change club", tapping a club opened its sign-up form, with the
     club's whole membership form in it, instead of the login it promises. */
  const pickClub = (club) => { rememberClub(club.id); setChosenId(String(club.id)); switchMode('login'); };
  const changeClub = () => { rememberClub(''); setChosenId(''); setClubId(''); switchMode('login'); };
  /* A registration link for a particular competition already knows where it
     is going, and one club is no choice at all. */
  const showPicker = clubsLoaded && regClubs.length > 1 && !chosenId && !regCompId;

  const registerClubId = React.useMemo(() => {
    if (clubId) return String(clubId);
    if (regClubs.length === 1) return String(regClubs[0].id);
    return '';
  }, [clubId, regClubs]);

  React.useEffect(() => {
    if (!registerClubId || mode !== 'register') {
      setRegisterMembershipMeta(null);
      return;
    }
    setRegisterMembershipLoading(true);
    fetch(`${API}/api/auth/clubs/${registerClubId}/membership-form/public`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Failed to load membership form');
        return data;
      })
      .then((data) => {
        setRegisterMembershipMeta(data || null);
        setRegisterMembershipForm({});
      })
      .catch(() => setRegisterMembershipMeta(null))
      .finally(() => setRegisterMembershipLoading(false));
  }, [registerClubId, mode]);

  React.useEffect(() => {
    if (!memberClubId) {
      setMembershipMeta(null);
      return;
    }
    setMembershipLoading(true);
    setMemberError('');
    fetch(`${API}/api/auth/clubs/${memberClubId}/membership-form/public`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Failed to load membership form');
        return data;
      })
      .then((data) => {
        setMembershipMeta(data || null);
        setMemberForm({});
        setMemberSuccess(false);
      })
      .catch((err) => {
        setMembershipMeta(null);
        setMemberError(err.message || 'Failed to load membership form');
      })
      .finally(() => setMembershipLoading(false));
  }, [memberClubId]);

  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [forgotSent, setForgotSent] = React.useState(false);

  const switchMode = (m) => { setMode(m); setError(''); setForgotSent(false); if (onSwitch) onSwitch(m); };
  /* "Bowling Start Year" is left out altogether — not shown, not required —
     for a player type the club does not ask it of (a social member, say).
     Worked out from the type picked so far, so it comes and goes as the
     player type changes. */
  const withoutStartYear = (meta, form) => field => !(field.key === START_YEAR_FIELD
    && !asksStartYear(typesWithoutStartYear(meta && meta.playerTypesWithoutStartYear, meta && meta.playerTypeOptions), form.playerType));
  const memberFields = ((membershipMeta && membershipMeta.form && Array.isArray(membershipMeta.form.fields)) ? membershipMeta.form.fields : [])
    .filter(withoutStartYear(membershipMeta, memberForm));
  const membershipEnabled = !!(membershipMeta && membershipMeta.form && membershipMeta.form.enabled);
  const membershipTypeOptions = (membershipMeta && Array.isArray(membershipMeta.membershipTypeOptions)) ? membershipMeta.membershipTypeOptions : [];
  const playerTypeOptions = (membershipMeta && Array.isArray(membershipMeta.playerTypeOptions)) ? membershipMeta.playerTypeOptions : [];
  const registerFields = ((registerMembershipMeta && registerMembershipMeta.form && Array.isArray(registerMembershipMeta.form.fields)) ? registerMembershipMeta.form.fields : [])
    .filter(withoutStartYear(registerMembershipMeta, registerMembershipForm));
  const registerMembershipEnabled = !!(registerMembershipMeta && registerMembershipMeta.form && registerMembershipMeta.form.enabled);
  const registerMembershipTypeOptions = (registerMembershipMeta && Array.isArray(registerMembershipMeta.membershipTypeOptions)) ? registerMembershipMeta.membershipTypeOptions : [];
  const registerMembershipTypePricing = normalizeMembershipTypePricing(
    registerMembershipMeta && registerMembershipMeta.membershipTypePricing,
    registerMembershipTypeOptions
  );
  const registerPlayerTypeOptions = (registerMembershipMeta && Array.isArray(registerMembershipMeta.playerTypeOptions)) ? registerMembershipMeta.playerTypeOptions : [];
  /* Age groups are one platform-wide list, so unlike the two above they do
     not vary with the club being applied to — the club's meta carries them
     only so this form needs no second request. */
  const ageGroupOptions = (membershipMeta && Array.isArray(membershipMeta.ageGroupOptions) && membershipMeta.ageGroupOptions.length)
    ? membershipMeta.ageGroupOptions
    : siteDefaults.ageGroupOptions;
  const registerAgeGroupOptions = (registerMembershipMeta && Array.isArray(registerMembershipMeta.ageGroupOptions) && registerMembershipMeta.ageGroupOptions.length)
    ? registerMembershipMeta.ageGroupOptions
    : siteDefaults.ageGroupOptions;
  const membershipTypePricing = normalizeMembershipTypePricing(
    membershipMeta && membershipMeta.membershipTypePricing,
    membershipTypeOptions
  );

  const optionsForMembershipField = (field) => {
    if (!field || field.type !== 'select') return [];
    if (Array.isArray(field.options) && field.options.length > 0) return field.options;
    if (field.optionsSource === 'membershipTypeOptions') return membershipTypeOptions;
    if (field.optionsSource === 'playerTypeOptions') return playerTypeOptions;
    if (field.optionsSource === 'ageGroupOptions') return ageGroupOptions;
    return [];
  };

  const optionsForRegisterField = (field) => {
    if (!field || field.type !== 'select') return [];
    if (Array.isArray(field.options) && field.options.length > 0) return field.options;
    if (field.optionsSource === 'membershipTypeOptions') return registerMembershipTypeOptions;
    if (field.optionsSource === 'playerTypeOptions') return registerPlayerTypeOptions;
    if (field.optionsSource === 'ageGroupOptions') return registerAgeGroupOptions;
    return [];
  };

  const valueForTypedField = (field, value) => {
    if (field && field.type === 'boolean') return !!value;
    return value || '';
  };

  const updateTypedFieldValue = (setter, field, rawValue) => {
    const nextValue = field && field.type === 'boolean' ? !!rawValue : rawValue;
    setter(f => ({ ...f, [field.key]: nextValue }));
  };

  const handleMemberSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setMemberError('');
    setMemberSubmitNotice('');
    setMemberDuplicate(null);
    setMemberSubmitting(true);
    try {
      const r = await fetch(`${API}/api/auth/membership/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clubId: memberClubId,
          data: memberForm,
          website: honeypotWebsite,
          alreadyMember: memberAlreadyMember === 'yes',
          accountMode: memberAccountMode,
          intent: memberAlreadyMember === 'yes' ? 'renew' : 'apply',
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 409 && data.duplicate) {
        setMemberDuplicate({ ...data.duplicate, message: data.error });
        setMemberSubmitting(false);
        return;
      }
      if (!r.ok) throw new Error(data.error || 'Failed to submit application');
      setMemberSuccess(true);
      if (memberAccountMode === 'silent-login') {
        if (data.accountSetupSent) {
          setMemberSubmitNotice('A GreenSheet login setup email has been sent. Use it to set your password.');
        } else if (data.accountSetupError) {
          setMemberSubmitNotice(`Your form was submitted, but login setup was not completed: ${data.accountSetupError}`);
        }
      }
      setMemberForm({});
      setHoneypotWebsite('');
    } catch (err) {
      setMemberError(err.message || 'Failed to submit application');
    }
    setMemberSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setClaimNotice('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        const r = await fetch(`${API}/api/auth/forgot-password`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Request failed');
        setForgotSent(true);
      } else if (mode === 'login') {
        await onLogin(email, password);
      } else {
        const effectiveClubId = registerClubId || clubId || undefined;
        if (!effectiveClubId) throw new Error('Please select your club');

        const membershipPayload = {
          ...registerMembershipForm,
          fullName: name,
          email,
        };

        if (registerMembershipEnabled) {
          for (const field of registerFields) {
            if (field.type === 'content') continue;
            const rawValue = membershipPayload[field.key];
            const v = field.type === 'boolean'
              ? (rawValue === true || rawValue === false ? String(rawValue) : '')
              : String(rawValue || '').trim();
            if (field.required && !v) {
              throw new Error(`Membership form: ${field.label} is required`);
            }
          }
        }

        const result = await onRegister(name, email, password, effectiveClubId, membershipPayload, 'apply');
        if (result && result.claimPending) {
          setClaimNotice(result.message);
          setPassword('');
        }
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto' }}>
      {/* Once a club is chosen the page is that club's, so its name leads;
          GreenSheet steps down to the line underneath. */}
      {chosenClub && !showPicker ? (
        <div className="auth-club-head">
          <div className="auth-club-name">{chosenClub.name}</div>
          <div className="auth-club-sub">
            on GreenSheet
            {regClubs.length > 1 && (
              <>
                <span aria-hidden="true"> · </span>
                <button type="button" className="auth-club-change" onClick={changeClub}>Change club</button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '32px 0 24px' }}>
          <div style={{ fontWeight: 700, fontSize: '2.4em', color: '#fff', letterSpacing: '1px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>GreenSheet</div>
          <div style={{ fontSize: '1.1em', color: 'rgba(255,255,255,0.8)' }}>Bowls Manager</div>
        </div>
      )}
      {/* Nothing until the clubs are known, so a site with several does not
          flash the login form before the picker replaces it. */}
      {showPicker ? <ClubPicker clubs={regClubs} onPick={pickClub} /> : (!clubsLoaded && !regCompId) ? null : (
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '48px', padding: '0 24px', flexWrap: 'wrap' }}>
        <div style={{ maxWidth: '520px', width: '100%', flex: '1 1 420px' }}>
          <div className="w3-card w3-round w3-white">
            {/* The two ways in, side by side, above everything.
              *
                This has now moved twice. It began as an underlined word at
                the end of the last line of small print; it became a button
                under a divider at the foot of the form; people looking for
                how to sign up still missed it, because by then they had
                read a password box and decided the page was not for them.
                A choice made after the form is a choice most people never
                reach. Both options are the first thing on the card now. */}
            <div className="w3-container w3-theme-d4 w3-round-top auth-head">
              <div className="auth-tabs" role="tablist" aria-label="Log in or create an account">
                <button type="button" role="tab" aria-selected={mode !== 'register'}
                  className={`auth-tab${mode !== 'register' ? ' auth-tab-on' : ''}`}
                  onClick={() => switchMode('login')}>
                  <i className="fa fa-sign-in w3-margin-right" aria-hidden="true"></i>Log in
                </button>
                <button type="button" role="tab" aria-selected={mode === 'register'}
                  className={`auth-tab${mode === 'register' ? ' auth-tab-on' : ''}`}
                  onClick={() => switchMode('register')}>
                  <i className="fa fa-user-plus w3-margin-right" aria-hidden="true"></i>Create an account
                </button>
              </div>
              {regCompId && <p style={{ margin: '8px 0 0', opacity: 0.8, fontSize: 'var(--gs-text-sm)' }}>Register to sign up for this competition</p>}
            </div>

            {mode === 'forgot' && forgotSent ? (
              <div className="w3-container w3-padding-24">
                <div className="w3-panel w3-pale-green w3-leftbar w3-border-green w3-round" style={{ padding: '12px 16px' }}>
                  <i className="fa fa-check-circle w3-text-green w3-margin-right"></i>
                  <strong>Check your email!</strong>
                  <p className="w3-small" style={{ margin: '8px 0 0' }}>
                    If that email is registered, we've sent you a link that signs you straight in. Check your inbox (and spam folder) — it works once, for 30 minutes.
                  </p>
                </div>
                <button type="button" className="w3-button w3-theme w3-round w3-block w3-margin-top"
                  onClick={() => switchMode('login')}>
                  <i className="fa fa-arrow-left w3-margin-right"></i>Back to Log In
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="w3-container w3-padding-24">
              {error && <div className="w3-panel w3-red w3-round w3-padding-small"><small>{error}</small></div>}
              {claimNotice && (
                <div className="w3-panel w3-pale-green w3-leftbar w3-border-green w3-round w3-padding-small" role="status">
                  <small><i className="fa fa-envelope w3-margin-right" aria-hidden="true"></i>{claimNotice}</small>
                </div>
              )}

              {mode === 'register' && (
                <>
                  {/* Club first: it decides which membership form appears below.
                      Not asked again when the visitor chose it on the way in —
                      it is at the top of the page, with "Change club". */}
                  {regClubs.length > 1 && !chosenClub && (
                    <>
                      <label className="w3-small w3-text-grey">Club</label>
                      <select className="w3-select w3-border w3-round w3-margin-bottom" value={clubId} onChange={e => setClubId(e.target.value)}>
                        <option value="">Select your club...</option>
                        {regClubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </>
                  )}
                  <label className="w3-small w3-text-grey">Full Name</label>
                  <input className="w3-input w3-border w3-round w3-margin-bottom" required value={name}
                    onChange={e => setName(e.target.value)} placeholder="e.g. John Smith" />

                  {registerMembershipLoading && (
                    <p className="w3-small w3-text-grey"><i className="fa fa-spinner fa-spin w3-margin-right"></i>Loading membership form...</p>
                  )}

                  {!registerMembershipLoading && registerMembershipEnabled && registerFields.length > 0 && (
                    <div className="w3-border w3-round" style={{ padding: '10px', marginBottom: '10px', background: '#f8faf8' }}>
                      {registerMembershipMeta && registerMembershipMeta.form && registerMembershipMeta.form.introText && (
                        <p className="w3-small w3-text-grey" style={{ marginTop: 0 }}>{registerMembershipMeta.form.introText}</p>
                      )}
                      {registerFields
                        .filter(field => field.key !== 'fullName' && field.key !== 'email')
                        .map(field => {
                          const value = valueForTypedField(field, registerMembershipForm[field.key]);
                          const options = optionsForRegisterField(field);
                          return (
                            <div key={field.key} className="w3-margin-bottom">
                              {field.type === 'content' ? (
                                <div style={{ margin: '4px 0 10px' }}>
                                  {field.label ? <p className="w3-small" style={{ margin: '0 0 4px', fontWeight: 700 }}>{field.label}</p> : null}
                                  {field.detail ? <p className="w3-small w3-text-grey" style={{ margin: 0, whiteSpace: 'pre-line' }}>{field.detail}</p> : null}
                                </div>
                              ) : (
                                <>
                              <label className="w3-small w3-text-grey">{field.label}{field.required ? ' *' : ''}</label>
                              {field.type === 'textarea' ? (
                                <textarea
                                  className="w3-input w3-border w3-round"
                                  rows={3}
                                  value={value}
                                  required={!!field.required}
                                  onChange={e => updateTypedFieldValue(setRegisterMembershipForm, field, e.target.value)}
                                />
                              ) : field.type === 'select' ? (
                                <>
                                <select
                                  className="w3-select w3-border w3-round"
                                  value={value}
                                  required={!!field.required}
                                  onChange={e => updateTypedFieldValue(setRegisterMembershipForm, field, e.target.value)}>
                                  <option value="">Select...</option>
                                  {options.map(opt => <option key={opt} value={opt}>{field.optionsSource === 'membershipTypeOptions' ? formatMembershipTypeOptionLabel(opt, registerMembershipTypePricing) : opt}</option>)}
                                </select>
                                {field.optionsSource === 'membershipTypeOptions' && (
                                  <MembershipTypeInfo type={value}
                                    descriptions={registerMembershipMeta && registerMembershipMeta.membershipTypeDescriptions}
                                    pricing={registerMembershipTypePricing} />
                                )}
                                </>
                              ) : field.type === 'boolean' ? (
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                                  <input
                                    type="checkbox"
                                    checked={!!value}
                                    onChange={e => updateTypedFieldValue(setRegisterMembershipForm, field, e.target.checked)}
                                  />
                                  <span className="w3-small">Yes</span>
                                </label>
                              ) : field.type === 'number' ? (
                                <input
                                  className="w3-input w3-border w3-round"
                                  type="number"
                                  value={value}
                                  required={!!field.required}
                                  onChange={e => updateTypedFieldValue(setRegisterMembershipForm, field, e.target.value)}
                                />
                              ) : field.type === 'date' ? (
                                <input
                                  className="w3-input w3-border w3-round"
                                  type="date"
                                  value={value}
                                  required={!!field.required}
                                  onChange={e => updateTypedFieldValue(setRegisterMembershipForm, field, e.target.value)}
                                />
                              ) : (
                                <input
                                  className="w3-input w3-border w3-round"
                                  type={field.type === 'email' ? 'email' : 'text'}
                                  value={value}
                                  required={!!field.required}
                                  onChange={e => updateTypedFieldValue(setRegisterMembershipForm, field, e.target.value)}
                                />
                              )}
                                </>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </>
              )}

              {mode === 'forgot' && (
                <p className="w3-small w3-text-grey" style={{ marginTop: 0, marginBottom: '16px' }}>
                  Enter your email and we'll send you a link that signs you in — no password needed. You can choose a new one once you're in.
                </p>
              )}

              <label className="w3-small w3-text-grey" htmlFor="auth-email">Email</label>
              <input id="auth-email" className="w3-input w3-border w3-round w3-margin-bottom" type="email" required value={email}
                onChange={e => setEmail(e.target.value)} placeholder="your@email.com" />

              {mode !== 'forgot' && (<>
                {/* Beside the password rather than under the button. This is
                    where it is looked for, and it is read by somebody whose
                    password has just failed them — after the Log In button
                    they have already given up and gone looking elsewhere. */}
                <div className="auth-label-row">
                  <label className="w3-small w3-text-grey" htmlFor="auth-password">Password</label>
                  {mode === 'login' && (
                    <button type="button" className="auth-forgot"
                      onClick={() => switchMode('forgot')}>Forgot your password?</button>
                  )}
                </div>
                <input id="auth-password" className="w3-input w3-border w3-round w3-margin-bottom" type="password" required minLength={mode === 'register' ? 8 : undefined}
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder={mode === 'register' ? 'At least 8 characters — a short phrase works well' : 'Your password'} />
              </>)}

              <button className="w3-button w3-theme w3-round w3-block w3-margin-top" type="submit" disabled={loading}>
                {loading ? <i className="fa fa-spinner fa-spin"></i> :
                  mode === 'forgot' ? <><i className="fa fa-paper-plane w3-margin-right"></i>Email me a sign-in link</> :
                  mode === 'login' ? <><i className="fa fa-sign-in w3-margin-right"></i>Log In</> :
                    <><i className="fa fa-user-plus w3-margin-right"></i>Create Account</>}
              </button>
              {mode === 'register' && (
                <>
                  {/* Said before the details are sent, not after: the Privacy Act asks that
                      people are told what happens to their information when it is collected. */}
                  <p className="privacy-notice">
                    Your club and GreenSheet use these details to run your membership and competitions, and never sell them or use them for advertising. Name, email and password are needed to sign in; anything else is optional unless marked *.
                    {' '}By creating an account you agree to the{' '}
                    <a href="/terms.html" target="_blank" rel="noopener">Terms of Use</a>. See{' '}
                    <a href="/privacy.html" target="_blank" rel="noopener">how we look after your information</a>.
                  </p>
                </>
              )}

              {/* The "New here? / Create an account" block that used to sit
                  here is gone: it is a tab at the top of the card now, where
                  it is seen before the form rather than after it. Forgot mode
                  keeps its way back, because that is not a choice between two
                  doors — it is an escape from a third. */}
              {mode === 'forgot' && (
                <div className="auth-switch">
                  <button type="button" className="auth-switch-link" onClick={() => switchMode('login')}>
                    Back to log in
                  </button>
                </div>
              )}
            </form>
            )}
          </div>

          <ClubNewsCard initialClubId={subscribeClubId} pageClubId={chosenClub ? chosenClub.id : ''} />

          {false && <div className="w3-card w3-round w3-white" style={{ marginTop: '16px' }}>
            <div className="w3-container w3-theme-d4 w3-round-top" style={{ padding: '12px 20px' }}>
              <h4 style={{ margin: 0 }}><i className="fa fa-id-card w3-margin-right"></i>Membership Application</h4>
              <p className="w3-small" style={{ margin: '4px 0 0', opacity: 0.85 }}>No GreenSheet login required</p>
            </div>
            <div className="w3-container w3-padding-16">
              {memberError && <div className="w3-panel w3-red w3-round w3-padding-small"><small>{memberError}</small></div>}
              {memberSuccess ? (
                <div>
                  <div className="w3-panel w3-pale-green w3-leftbar w3-border-green w3-round" style={{ padding: '12px 16px' }}>
                    <i className="fa fa-check-circle w3-text-green w3-margin-right"></i>
                    <strong>Application submitted</strong>
                    <p className="w3-small" style={{ margin: '8px 0 0' }}>Your membership is now pending. A club admin must confirm it before it becomes active.</p>
                    {memberSubmitNotice && <p className="w3-small" style={{ margin: '8px 0 0' }}>{memberSubmitNotice}</p>}
                  </div>
                  <button type="button" className="w3-button w3-theme w3-round"
                    onClick={() => { setMemberSuccess(false); setMemberError(''); setMemberSubmitNotice(''); }}>
                    Submit another application
                  </button>
                </div>
              ) : (
                <form onSubmit={handleMemberSubmit}>
                  <label className="w3-small w3-text-grey">Club</label>
                  <select className="w3-select w3-border w3-round w3-margin-bottom"
                    value={memberClubId}
                    onChange={e => setMemberClubId(e.target.value)}
                    required>
                    <option value="">Select a club...</option>
                    {regClubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>

                  {!memberClubId && <p className="w3-small w3-text-grey">Choose a club to load its application form.</p>}
                  {membershipLoading && <p className="w3-small w3-text-grey"><i className="fa fa-spinner fa-spin w3-margin-right"></i>Loading form...</p>}
                  {!membershipLoading && memberClubId && membershipMeta && !membershipEnabled && (
                    <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-amber w3-round">
                      This club is not accepting online membership applications right now.
                    </div>
                  )}

                  {!membershipLoading && membershipEnabled && (
                    <>
                      <div className="w3-margin-bottom">
                        <label className="w3-small w3-text-grey"><strong>Already a member at this club?</strong></label>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '6px', flexWrap: 'wrap' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <input
                              type="radio"
                              name="memberAlreadyMember"
                              checked={memberAlreadyMember === 'yes'}
                              onChange={() => setMemberAlreadyMember('yes')}
                            />
                            <span className="w3-small">Yes, renew my existing profile</span>
                          </label>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <input
                              type="radio"
                              name="memberAlreadyMember"
                              checked={memberAlreadyMember === 'no'}
                              onChange={() => setMemberAlreadyMember('no')}
                            />
                            <span className="w3-small">No, I am new</span>
                          </label>
                        </div>
                        {memberAlreadyMember === 'yes' && (
                          <p className="w3-small w3-text-grey" style={{ margin: '6px 0 0' }}>
                            Use the same email as your existing club profile so we renew that record and avoid duplicates.
                          </p>
                        )}
                      </div>

                      {membershipMeta.form.introText && (
                        <p className="w3-small w3-text-grey" style={{ marginTop: '4px' }}>{membershipMeta.form.introText}</p>
                      )}
                      {memberFields.map(field => {
                        const value = valueForTypedField(field, memberForm[field.key]);
                        const options = optionsForMembershipField(field);
                        return (
                          <div key={field.key} className="w3-margin-bottom">
                            {field.type === 'content' ? (
                              <div style={{ margin: '4px 0 10px' }}>
                                {field.label ? <p className="w3-small" style={{ margin: '0 0 4px', fontWeight: 700 }}>{field.label}</p> : null}
                                {field.detail ? <p className="w3-small w3-text-grey" style={{ margin: 0, whiteSpace: 'pre-line' }}>{field.detail}</p> : null}
                              </div>
                            ) : (
                              <>
                            <label className="w3-small w3-text-grey">{field.label}{field.required ? ' *' : ''}</label>
                            {field.type === 'textarea' ? (
                              <textarea
                                className="w3-input w3-border w3-round"
                                rows={3}
                                value={value}
                                required={!!field.required}
                                onChange={e => updateTypedFieldValue(setMemberForm, field, e.target.value)}
                              />
                            ) : field.type === 'select' ? (
                              <>
                              <select
                                className="w3-select w3-border w3-round"
                                value={value}
                                required={!!field.required}
                                onChange={e => updateTypedFieldValue(setMemberForm, field, e.target.value)}>
                                <option value="">Select...</option>
                                {options.map(opt => <option key={opt} value={opt}>{field.optionsSource === 'membershipTypeOptions' ? formatMembershipTypeOptionLabel(opt, membershipTypePricing) : opt}</option>)}
                              </select>
                              {field.optionsSource === 'membershipTypeOptions' && (
                                <MembershipTypeInfo type={value}
                                  descriptions={membershipMeta && membershipMeta.membershipTypeDescriptions}
                                  pricing={membershipTypePricing} />
                              )}
                              </>
                            ) : field.type === 'boolean' ? (
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                                <input
                                  type="checkbox"
                                  checked={!!value}
                                  onChange={e => updateTypedFieldValue(setMemberForm, field, e.target.checked)}
                                />
                                <span className="w3-small">Yes</span>
                              </label>
                            ) : field.type === 'number' ? (
                              <input
                                className="w3-input w3-border w3-round"
                                type="number"
                                value={value}
                                required={!!field.required}
                                onChange={e => updateTypedFieldValue(setMemberForm, field, e.target.value)}
                              />
                            ) : field.type === 'date' ? (
                              <input
                                className="w3-input w3-border w3-round"
                                type="date"
                                value={value}
                                required={!!field.required}
                                onChange={e => updateTypedFieldValue(setMemberForm, field, e.target.value)}
                              />
                            ) : (
                              <input
                                className="w3-input w3-border w3-round"
                                type={field.type === 'email' ? 'email' : 'text'}
                                value={value}
                                required={!!field.required}
                                onChange={e => updateTypedFieldValue(setMemberForm, field, e.target.value)}
                              />
                            )}
                              </>
                            )}
                          </div>
                        );
                      })}

                      <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
                        <label>Website</label>
                        <input
                          type="text"
                          autoComplete="off"
                          tabIndex={-1}
                          value={honeypotWebsite}
                          onChange={e => setHoneypotWebsite(e.target.value)}
                        />
                      </div>

                      <div className="w3-margin-bottom">
                        <label className="w3-small w3-text-grey"><strong>To manage your account, create a login and password?</strong></label>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '6px', flexWrap: 'wrap' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <input
                              type="radio"
                              name="memberAccountMode"
                              checked={memberAccountMode === 'silent-login'}
                              onChange={() => setMemberAccountMode('silent-login')}
                            />
                            <span className="w3-small">Yes, create a login and password</span>
                          </label>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <input
                              type="radio"
                              name="memberAccountMode"
                              checked={memberAccountMode === 'guest'}
                              onChange={() => setMemberAccountMode('guest')}
                            />
                            <span className="w3-small">No, continue as guest only</span>
                          </label>
                        </div>
                        {memberAccountMode === 'silent-login' && (
                          <p className="w3-small w3-text-grey" style={{ margin: '6px 0 0' }}>
                            We will email you a secure link to create your password after you submit.
                          </p>
                        )}
                      </div>

                      {/* This address already has a profile at this club, so
                          the form stops. There is deliberately no way to
                          send it anyway: this page is open to anybody, and
                          "yes, that is me" is not something an anonymous
                          visitor can be asked to certify about somebody
                          else's record. Every route offered here proves the
                          address belongs to whoever is using it, because
                          each one arrives by email. */}
                      {memberDuplicate && (
                        <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-orange w3-round"
                          style={{ padding: '10px 14px' }} role="alert">
                          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>{memberDuplicate.message}</p>
                          <p style={{ margin: '0 0 10px' }}>
                            {memberDuplicate.pending
                              ? 'If that was you, the club has it and there is nothing more to do — they will be in touch. If something needs changing, contact the club.'
                              : 'If that is you, sign in to update your details, or renew your membership below.'}
                            {' '}
                            <strong>If you are filling this in for somebody else, they need an email address of their own</strong>
                            {' '}— two people cannot share one membership record.
                          </p>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {memberDuplicate.suggestRenew && (
                              <button type="button" className="w3-button w3-round w3-border"
                                onClick={() => { setMemberDuplicate(null); setMemberAlreadyMember('yes'); }}>
                                Renew my membership
                              </button>
                            )}
                            <button type="button" className="w3-button w3-round w3-border"
                              onClick={() => { setMemberDuplicate(null); switchMode('forgot'); }}>
                              Sign in or reset my password
                            </button>
                            {membershipMeta && membershipMeta.club && membershipMeta.club.email && (
                              <a className="w3-button w3-round w3-border"
                                href={`mailto:${membershipMeta.club.email}`}>
                                Email the club
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      <button className="w3-button w3-theme w3-round w3-block" type="submit" disabled={memberSubmitting}>
                        {memberSubmitting ? <><i className="fa fa-spinner fa-spin w3-margin-right"></i>Submitting...</> : <><i className="fa fa-paper-plane w3-margin-right"></i>{memberAlreadyMember === 'yes' ? 'Submit Membership Renewal' : 'Submit Membership Application'}</>}
                      </button>
                      {/* Said before the details are sent, not after: the Privacy Act asks that
                          people are told what happens to their information when it is collected. */}
                      <p className="privacy-notice">
                        Your club uses these details to manage your membership; some answers are emailed to the club and not stored. Only questions marked * are required.{' '}
                        <a href="/privacy.html" target="_blank" rel="noopener">How we look after your information</a>
                      </p>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>}
        </div>

      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', padding: '20px 0' }}>
        {clubLogoUrl(chosenClub) && brokenLogo !== chosenClub.id ? (
          <span className="auth-club-logo">
            <img src={clubLogoUrl(chosenClub)} alt={`${chosenClub.name} logo`}
              onError={() => setBrokenLogo(chosenClub.id)} />
          </span>
        ) : (
          <img src="/icon.svg" alt="GreenSheet" style={{ width: '280px', height: '280px', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.3))' }} />
        )}
      </div>
      </div>
      )}
    </div>
  );
}

export default AuthScreen;
